import { useEffect, useState, useRef, useMemo } from "react";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import { Typography, Box, Grid, Button, Paper, Tooltip, Icon } from "@mui/material";
import { useNavigate } from "react-router-dom";

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import LoadingScreen from "components/Loading";
import ConfirmationDialog from "components/ConfirmationDialog";
import { Bar } from "react-chartjs-2";
import { useAuth } from "context/authContext";
import {
  getCompetenciesForManager,
  getCompetencyLevelsForJobOnDate,
  deleteTrainingRecordById,
  getCompMyTeamPageData,
} from "services/api";
import { useTranslation } from "react-i18next";

import SnackbarWrapper from "components/Snackbar";
import { drawZeroBarsPlugin } from "services/utils";
import { useAppTheme } from "context/themeContext";

const TrainingsTab = () => {
  const { t } = useTranslation();
  const [loadingTeamData, setLoadingTeamData] = useState(true);
  const [loadingSelectedEmp, setLoadingSelectedEmp] = useState(false);
  const { employee, selectedMonth, selectedYear } = useAuth();
  const [employeeCompGaps, setEmployeeCompGaps] = useState({});
  const snackbarRef = useRef();
  const navigate = useNavigate();
  const { primaryColor } = useAppTheme();
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [selectedEmpTraining, setSelectedEmpTraining] = useState([]);
  const [jobLevels, setJobLevels] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [originalRows, setOriginalRows] = useState({});
  const [employeeTrainingOptions, setEmployeeTrainingOptions] = useState({});
  const [confOpen, setConfOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState({ id: null, index: null });
  const [draftRows, setDraftRows] = useState({});
  const [trainingMap, setTrainingMap] = useState({});
  const [trainingToCompMap, setTrainingToCompMap] = useState({});
  const [selectedLevelIds, setSelectedLevelIds] = useState({});

  const [employeeHistory, setEmployeeHistory] = useState([employee.id]);
  const currentEmpId = employeeHistory[employeeHistory.length - 1];

  ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,

    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    drawZeroBarsPlugin
  );

  const toggleEditing = (index) => {
    setEditingRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));

    if (!editingRows[index]) {
      // entering edit mode → save original row for comparison
      setOriginalRows((prev) => ({
        ...prev,
        [index]: [...selectedEmpTraining[index]],
      }));
    }
  };

  const handleViewTeam = (empId) => {
    setEmployeeHistory((prev) => [...prev, empId]);
  };

  //For showing the expected level when opening edit mode
  useEffect(() => {
    const updated = {};
    selectedEmpTraining?.forEach((row, index) => {
      if (editingRows[index]) {
        updated[index] = row[4] || ""; // Expected Level ID
      }
    });
    setSelectedLevelIds((prev) => ({ ...prev, ...updated }));
  }, [editingRows, selectedEmpTraining]);

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteTrainingRecordById(rowToDelete.id);
      if (res !== "error") {
        setSelectedEmpTraining((prev) => {
          const updated = [...prev];
          updated.splice(rowToDelete.index, 1);
          return updated;
        });
        snackbarRef.current?.showSnackbar(t("user.trainings.deletedSuccess"), "success");
      } else {
        // console.error("API returned error");
        snackbarRef.current?.showSnackbar(t("user.trainings.deletedFailed"), "error");
      }
    } catch (err) {
      // console.error("Delete error:", err);
      snackbarRef.current?.showSnackbar(t("user.trainings.deletedFailed"), "error");
    }

    setConfOpen(false); // always close dialog
  };

  const handleBack = () => {
    if (employeeHistory?.length > 1) {
      setEmployeeHistory((prev) => prev.slice(0, -1)); // Remove last employee
    }
  };

  const handleEmployeeChartClick = async (empId) => {
    //Select/Deselect Employee
    const newSelectedId = selectedEmpId === empId ? null : empId;
    setSelectedEmpId(newSelectedId);

    if (newSelectedId !== null) {
      setLoadingSelectedEmp(true);
      try {
        const data = await getCompMyTeamPageData(empId, selectedMonth, selectedYear);
        console.log("Data In CompMyTeam::", data);
        //1.Get training data for the selected emp
        const trainingData = data.trainings;
        console.log("Training Data:", trainingData);
        setSelectedEmpTraining(trainingData); //training data for the selected emp
        const levelIdsMap = {};
        trainingData?.forEach((row, idx) => {
          levelIdsMap[idx] = row[4] || ""; // row[4] is the levelId
        });
        setSelectedLevelIds(levelIdsMap); // This is a useState to control each dropdown

        //creates a deep copy of trainingData array and stores it in the originalRows state
        //so that when user changes trainining data we have a copy for the originial that is not changed
        setOriginalRows(trainingData.map((row) => [...row]));
        setDraftRows((prev) => ({
          ...prev,
          [empId]: {
            competencyId: "",
            trainingId: "", // selected training for the comp
            startDate: "",
            endDate: "",
            levelId: "",
          },
        }));

        //2.Gets a list: trainingId, trainingName, and compId (trainings and their comps)
        // const allTrainings = await getAllCompToTraining();
        const allTrainings = data.compToTrainings;
        if (allTrainings !== "error") {
          const trainingMap = {};
          const trainingToCompMap = {};

          allTrainings?.forEach((item) => {
            const { compId, id, trainingName } = item;
            trainingToCompMap[item.id] = item.compId;
            if (!trainingMap[compId]) {
              trainingMap[compId] = [];
            }
            // trainingMap[compId].push(trainingName);
            trainingMap[compId].push({
              id,
              name: item.trainingName,
            });
          });

          //stores all trainings for all comps not for an employee's specific comps
          setTrainingMap(trainingMap); //trainings for each comp,compId:Trainings
          setTrainingToCompMap(trainingToCompMap); // trainingId => compId

          const empGaps = employeeCompGaps[empId] || {}; //Get the selected employee's competency gaps
          const seenTrainings = new Set();
          const trainingList = [];

          //3.Build a list of all trainings related to that employee’s competencies (comps related to his job)
          //Loop through each compId for the current employee.
          Object.keys(empGaps).forEach((compId) => {
            //From trainingMap, get the list of trainings related to this compId
            const compTrainings = trainingMap[compId] || [];
            compTrainings?.forEach((training) => {
              const key = `${training.id}-${training.name}`;
              if (!seenTrainings.has(key)) {
                seenTrainings.add(key);
                trainingList.push(training);
              }
            });
          });

          setEmployeeTrainingOptions((prev) => ({
            ...prev,
            [empId]: trainingList,
          }));

          //4.Fetches competency levels for the job of the selected emp
          const jobId = Object.values(empGaps)[0]?.jobId; //extracts job Id from emp-comp gap data

          if (jobId) {
            const levels = await getCompetencyLevelsForJobOnDate(
              jobId,
              selectedMonth,
              selectedYear
            );
            const formatted = levels.map((level) => ({
              id: level[0],
              name: level[1],
              value: level[2],
              competencyId: level[3],
            }));
            setJobLevels({ [jobId]: formatted });

            navigate("/learning-and-development", {
              state: {
                selectedEmployeeId: empId,
                selectedEmployeeTraining: trainingData,
                mapTraining: trainingMap,
                trainingCompMap: trainingToCompMap,
                empTrainingOptions: trainingList,
                jobLvls: formatted,
                empCompGaps: empGaps,
                selectedLvlIds: levelIdsMap,
              },
            });
          }
        } else {
          snackbarRef.current?.showSnackbar(t("user.trainings.trainingLoadFailed"), "error");
        }
      } catch (error) {
        console.error("Failed to fetch training data", error);
      } finally {
        setLoadingSelectedEmp(false); // ✅ MODIFIED: ensure loading false after try-catch
      }
    } else {
      setSelectedEmpTraining([]);
      setOriginalRows([]);
    }
  };

  useEffect(() => {
    setSelectedEmpId(null);
    setSelectedEmpTraining([]);
    setOriginalRows([]);

    const fetchAllCompetencyData = async () => {
      setLoadingTeamData(true);
      try {
        // Getting Employees under the manager and their CompToEmp data
        const data = await getCompetenciesForManager(currentEmpId, selectedYear, selectedMonth);
        console.log("Data::", data);
        if (data !== "error") {
          const competencyGaps = {};
          // Making a map for employees: for each emp,for each one of his comp: show the emp's gap in it
          data?.forEach((row) => {
            const compId = row[0];
            const compName = row[1];
            const targetValue = row[6]; // job-level value
            const empId = String(row[7] ?? row[17]);
            const currentValue = row[13]; // employee-level value (nullable)
            const empName = row[14];
            const jobId = row[15];

            // const gap = targetValue ? (currentValue ?? 0) - targetValue : 0;

            // NEW LOGIC:
            // Only calculate a gap if BOTH values are strictly not null/undefined
            let gap = null;
            if (
              targetValue !== null &&
              targetValue !== undefined &&
              currentValue !== null &&
              currentValue !== undefined
            ) {
              gap = currentValue - targetValue;
            }

            if (!competencyGaps[empId]) competencyGaps[empId] = {};
            //for each emp,for each comp: show the emp's gap in it
            competencyGaps[empId][compId] = {
              competencyName: compName,
              gap,
              empName,
              jobId,
            };
          });
          setEmployeeCompGaps(competencyGaps);
        } else {
          snackbarRef.current?.showSnackbar(t("user.trainings.unexpectedError"), "error");
        }
      } catch (e) {
        snackbarRef.current?.showSnackbar(t("user.trainings.unexpectedError"), "error");
      } finally {
        setLoadingTeamData(false);
      }
    };

    fetchAllCompetencyData();
  }, [currentEmpId, selectedMonth, selectedYear]);

  //calculates the average competency gap for each competency across all employees
  const { avgGapLabels, avgGapData, avgGapColors } = useMemo(() => {
    const gapMap = {};
    const countMap = {};

    Object.values(employeeCompGaps).forEach((empMap) => {
      Object.values(empMap).forEach(({ competencyName, gap }) => {
        if (gap == null) return;
        if (!gapMap[competencyName]) {
          gapMap[competencyName] = 0;
          countMap[competencyName] = 0;
        }
        gapMap[competencyName] += gap;
        countMap[competencyName] += 1;
      });
    });

    const entries = Object.keys(gapMap)
      .filter((name) => name !== "null" && name !== null) // skip "null"
      .map((name) => ({
        competency: name,
        avgGap: gapMap[name] / countMap[name],
      }));
    return {
      avgGapLabels: entries.map((e) => e.competency),
      avgGapData: entries.map((e) => e.avgGap),
      avgGapColors: entries.map((e) => (e.avgGap >= 0 ? "#108092" : "#ef5350")),
    };
  }, [employeeCompGaps]);

  if (loadingTeamData || loadingSelectedEmp) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ px: 2 }}>
      {/* Month & Year Selector */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {employeeHistory?.length > 1 && (
          <Button
            startIcon={<Icon>arrow_back</Icon>}
            sx={{
              color: primaryColor,
              borderColor: primaryColor,
              "&:hover": { backgroundColor: primaryColor, color: "#fff" },
            }}
            onClick={handleBack}
          >
            {t("user.myTeam.back")}
          </Button>
        )}
      </Grid>

      <Typography variant="h4" fontWeight="bold" align="center" sx={{ mb: 4 }}>
        {t("user.trainings.title")}
      </Typography>

      <Grid container spacing={3} sx={{ display: "flex", flexWrap: "wrap" }}>
        {/* Looping over emp-competency gap map,iterating over each item in the array (emp) by destructuring it to
      key-value pairs: empId and the object inside emp id (compId and the details) */}
        {Object.entries(employeeCompGaps).map(([empId, gapsMap]) => {
          //So for each employee, gapsMap holds that employee's competency data, and competencies extracts just the values
          //for each compId
          const competencies = Object.values(gapsMap);
          if (!competencies || competencies?.length === 0) return null;
          if (selectedEmpId !== null && selectedEmpId !== empId) return null;

          const employeeName = competencies[0].empName ?? `Employee ${empId}`;
          const labels = competencies.map((c) => c.competencyName); //creates a new array that contains only the names of the comps
          const gapData = competencies.map((c) => c.gap); //extracts the gap values from each competency object

          const barData = {
            labels,
            datasets: [
              {
                // label: "Gap",
                label: t("user.trainings.gap"),
                data: gapData,
                // backgroundColor: gapData.map((g) => (g >= 0 ? "#108092" : "#ef5350")),
                backgroundColor: gapData.map((g) => {
                  if (g === null) return "rgba(0,0,0,0)"; // Transparent for null values
                  return g >= 0 ? "#108092" : "#ef5350";
                }),
              },
            ],
          };

          const barOptions = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                top: 10,
                bottom: 10,
                left: 5,
                right: 5,
              },
            },
            plugins: {
              title: {
                display: true,
                text: `${employeeName}`,
                font: {
                  size: 18,
                  weight: "bold",
                  family: "Arial",
                },
                color: "#334155",
                padding: {
                  top: 10,
                  bottom: 20,
                },
              },
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.raw ?? 0;
                    const status =
                      value >= 0 ? "user.trainings.aboveTarget" : "user.trainings.belowTarget";
                    const icon = value >= 0 ? "▲" : "▼";
                    return [
                      `${t("user.trainings.chartsY")}: ${value}`,
                      `${icon} ${t("user.myScorecard.status")}: ${t(status)}`,
                    ];
                  },
                },
              },
            },
            scales: {
              x: {
                ticks: {
                  autoSkip: true,
                  maxRotation: 0,
                  minRotation: 0,
                  display: false,
                },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  stepSize: 1,
                  callback: (val) => val,
                  color: "#374151",
                  font: {
                    size: 13,
                    weight: "bold",
                    family: "Arial",
                  },
                },
                grid: {
                  color: "#e0e0e0",
                  borderDash: [4, 4],
                },
              },
            },
          };

          return (
            <Grid
              item
              key={empId}
              xs={12}
              // sm={12}
              sm={selectedEmpId === empId ? 12 : 12}
              md={selectedEmpId === empId ? 12 : 6}
              lg={selectedEmpId === empId ? 12 : 3}
              xl={selectedEmpId === empId ? 12 : 3}
            >
              <Paper
                onClick={() => handleEmployeeChartClick(empId)}
                sx={{
                  width: "100%",
                  maxWidth: "100%", // prevents overflow on very small screens
                  minHeight: {
                    xs: 360, // mobile
                    sm: 380,
                    md: 400,
                    lg: 420, // desktop
                  },
                  width: "100%",
                  overflow: "hidden",
                  p: 2,
                  borderRadius: "30px",
                  background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
                  boxShadow: `
                  10px 10px 20px rgba(0,0,0,0.15),   // deeper shadow
  -10px -10px 20px rgba(255,255,255,0.9) // highlight
                `,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                  },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {/* Chart container */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box
                      sx={{
                        width: "100%",
                        overflowX: { xs: "auto", md: "visible" }, // scroll only on mobile
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: { xs: Math.max(labels.length * 60, 300), md: "100%" },
                          height: { xs: 200, sm: 240, md: 280, lg: 300 },
                          position: "relative",
                        }}
                      >
                        {gapData.length ? (
                          <Bar data={barData} options={barOptions} />
                        ) : (
                          <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 10, color: "#94a3b8" }}
                          >
                            {t("user.trainings.noData")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Labels container */}
                  <Box
                    display="flex"
                    justifyContent="space-around"
                    alignItems="center"
                    mt={1}
                    sx={{
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      minHeight: 28, // keeps row height same
                    }}
                  >
                    {labels.map((label, i) => (
                      <Tooltip title={label} key={i}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 600, color: "#334155" }}
                        >
                          {label?.length > 7 ? label?.slice(0, 7) + "…" : label}
                        </Typography>
                      </Tooltip>
                    ))}
                  </Box>

                  <Box
                    display="flex"
                    gap={1}
                    mt={1}
                    sx={{
                      flexDirection: { xs: "column", sm: "row" }, // stacked on mobile, side by side on desktop
                    }}
                  >
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      startIcon={<Icon fontSize="small">groups</Icon>}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewTeam(empId);
                      }}
                      sx={{
                        flex: 1, // equal width
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t("user.myTeam.viewTeam")}
                    </Button>

                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      startIcon={<Icon fontSize="small">assessment</Icon>}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/team/competencies", {
                          state: { employeeId: empId, employeeName: employeeName },
                        });
                      }}
                      sx={{
                        flex: 1, // equal width
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t("user.myTeam.viewComps")}
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* Draft Row in Separate Paper New Table */}
            </Grid>
          );
        })}
      </Grid>

      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {/* Team Average Gap Analysis */}

      {selectedEmpId === null && (
        <Box sx={{ mt: 6, display: "flex", justifyContent: "center", px: { xs: 2, sm: 3, md: 0 } }}>
          <Paper
            // sx={{ p: 3, width: "100%", maxWidth: 900 }}
            sx={{
              p: { xs: 2, sm: 3 },
              width: "100%",
              maxWidth: { xs: "100%", sm: 700, md: 900 },
              borderRadius: "20px",
              background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
              boxShadow: `
   8px 8px 16px rgba(0, 0, 0, 0.05),
   -4px -4px 12px rgba(255, 255, 255, 0.6)
   `,
              transition: "box-shadow 0.3s ease-in-out",
              "&:hover": {
                boxShadow: `
        12px 12px 24px rgba(0, 0, 0, 0.1),
        -6px -6px 18px rgba(255, 255, 255, 0.8)
      `,
              },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "100%", // always fill Paper
                // overflowX: avgGapLabels.length > 10 ? "auto" : "hidden", // scroll only if many bars
                overflowX: "auto",
              }}
            >
              <div
                style={{
                  width: "100%", // fill Paper
                  maxWidth: "100%", // 🔹 MODIFIED: prevents overflow on small screens
                  height: window.innerWidth >= 1024 ? 400 : 350, // 🔹 MODIFIED: taller desktop, smaller mobile
                }}
              >
                <Bar
                  data={{
                    labels: avgGapLabels,
                    datasets: [
                      {
                        label: "Average Gap (Current - Target)",
                        data: avgGapData,
                        backgroundColor: avgGapColors,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    aspectRatio: 2,
                    plugins: {
                      title: {
                        display: true,
                        // text: "Team Average Gap Analysis",
                        text: t("user.trainings.teamChart"),
                        color: "#334155",
                        font: {
                          size: 18,
                          weight: "bold",
                          family: "Arial",
                        },
                        padding: {
                          top: 10,
                          bottom: 20,
                        },
                      },
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) =>
                            `${t("user.trainings.avgGap")}: ${context.raw.toFixed(2)}`,
                        },
                      },
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          // text: "Competencies",
                          text: t("user.trainings.chartsX"),
                          align: "center", // Center the title
                          font: {
                            size: 14,
                            weight: "bold",
                            family: "Arial",
                          },
                          color: "#334155", // Same color as chart title
                          padding: { top: 15 },
                        },
                        ticks: {
                          stepSize: 1,
                          callback: function (val, index) {
                            const label = this.getLabelForValue(index);
                            return label?.length > 20 ? label?.slice(0, 20) + "…" : label;
                          },
                          maxRotation: 0,
                          minRotation: 0,
                          color: "#374151", // Darker text for better contrast
                          font: {
                            size: 13,
                            weight: "bold",
                            family: "Arial",
                          },
                        },
                        grid: {
                          color: "#e0e0e0",
                          borderDash: [4, 4],
                        },
                      },
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          // text: "Gap",
                          text: t("user.trainings.chartsY"),
                          align: "center", // Not always visually centered vertically, but applies alignment
                          font: {
                            size: 14,
                            weight: "bold",
                            family: "Arial",
                          },
                          color: "#334155",
                          padding: { bottom: 10 },
                        },

                        ticks: {
                          stepSize: 0.5,
                          callback: (val) => val,
                          color: "#374151", // Tailwind gray-700 for better visibility
                          font: {
                            size: 13,
                            weight: "bold",
                            family: "Arial",
                          },
                        },
                        grid: {
                          color: "#e0e0e0",
                          borderDash: [4, 4],
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </Paper>
        </Box>
      )}

      {/* Optional: Snackbar for messages */}
      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </Box>
  );
};

export default TrainingsTab;
